import {
  BodyLink,
  BodyText,
  Caption,
  Flex,
  Spacer,
  Stack
} from '@kvdbil/components';
import React, { useState } from 'react';
import {
  MONTHLY_COST_DOWNPAYMENT_PERCENT,
  MONTHLY_COST_RESIDUAL_VALUE_MONTHS
} from '~/config/constants';
import { formatMoney } from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import { globalInterestRate as globalInterestRateSelector } from '~/App/shared/selectors/finance';
import { useSelector } from 'react-redux';
import { UnmountClosed } from 'react-collapse';
import ToggleArrow from '~/App/shared/components/ToggleArrow';
import { LoanWarning } from '~/App/views/Checkout/components/Configure/LoanWarning';
import { useObjectPageCMSData } from '~/App/views/ObjectPage/hooks';
import { getMonthlyLoanCost } from '../../helpers';

export interface Props {
  bidAmount: string;
}

export const BidFormMonthlyCost = ({ bidAmount }: Props) => {
  const { t } = useTranslation();
  const { data: cmsData } = useObjectPageCMSData();
  const globalInterestRate = useSelector(globalInterestRateSelector);
  const [isLoanCostExpanded, setIsLoanCostExpanded] = useState(false);

  const downPayment =
    parseInt(bidAmount, 10) * MONTHLY_COST_DOWNPAYMENT_PERCENT;
  const estimatedLoan = parseInt(bidAmount, 10) - downPayment;

  const monthlyCost = getMonthlyLoanCost({
    amount: parseInt(bidAmount, 10),
    interestRate: globalInterestRate
  });

  const toggleExpanded = () => setIsLoanCostExpanded(!isLoanCostExpanded);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <Flex
        justify="space-between"
        align="center"
        style={{ cursor: 'pointer' }}
        onClick={toggleExpanded}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isLoanCostExpanded}
        aria-controls="bid-form-monthly-cost-content"
      >
        <BodyText as="span">{t('Estimated loan cost')}</BodyText>
        <Flex align="center">
          <BodyLink as="span" aria-live="polite" aria-atomic="true">
            {bidAmount
              ? `${formatMoney({
                  value: monthlyCost,
                  currency: t('sek/month')
                })}*`
              : '-'}
          </BodyLink>

          <ToggleArrow isExpanded={isLoanCostExpanded} />
        </Flex>
      </Flex>

      <UnmountClosed isOpened={isLoanCostExpanded}>
        <Spacer height={0.5} />

        <Stack spacing={0.5} id="bid-form-monthly-cost-content">
          <Caption>{cmsData?.buyMethods?.loanCalculationExplanation}</Caption>

          <div>
            <Flex justify="space-between">
              <Caption>{t('Estimated loan')}</Caption>
              <Caption>
                {formatMoney({ value: estimatedLoan, currency: t('SEK') })}
              </Caption>
            </Flex>
            <Flex justify="space-between">
              <Caption>{t('Repayment period')}</Caption>
              <Caption>{`${MONTHLY_COST_RESIDUAL_VALUE_MONTHS} ${t(
                'months'
              )}`}</Caption>
            </Flex>
            <Flex justify="space-between">
              <Caption>{t('Preliminary interest rate (variable)')}</Caption>
              <Caption>{`${globalInterestRate} %`}</Caption>
            </Flex>
          </div>

          {cmsData?.buyMethods?.loanWarning && (
            <LoanWarning content={cmsData?.buyMethods?.loanWarning} />
          )}
        </Stack>
      </UnmountClosed>
    </div>
  );
};

export default BidFormMonthlyCost;
